import React, { useState, useRef, useEffect } from "react";
import Image from "next/image";

const initiatives = [
  {
    id: 1,
    title: "Positive Vibes",
    image: "/images/positive_vibes.jpg",
    description:
      "The Positive Vibes Campaign is bringing together kids from different countries to make an impact in the world through art and music. They will use funds raised to support schools in underserved communities with vital school supplies.",
    user: "#F6DBB4",
  },
  {
    id: 2,
    title: "World of Wonder",
    image: "/images/world_of_wonder.jpg",
    description:
      "World of Wonder connects children with nature through immersive educational experiences. This initiative aims to build outdoor classrooms and wildlife observation areas in urban schools, fostering environmental awareness from an early age.",
    user: "#F0640F",
  },
  {
    id: 3,
    title: "Water For Life",
    image: "/images/water_for_life.jpg",
    description:
      "Water For Life is working to provide clean drinking water to communities facing water scarcity. The initiative funds well construction, water purification systems, and educational programs about sustainable water management practices.",
    user: "#C6E084",
  },
  {
    id: 4,
    title: "Canine Superheroes",
    image: "/images/canine_superheroes.jpg",
    description:
      "Canine Superheroes trains service dogs to assist children with disabilities. This initiative covers the costs of dog training, and ongoing support to help create life-changing partnerships between children and their canine companions.",
    user: "#0085C0",
  },
  {
    id: 5,
    title: "Road to Education",
    image: "/images/road_to_education.jpg",
    description:
      "Road to Education provides safe transportation for children in remote areas to reach their schools. The initiative funds buses, bicycles, and road improvements to reduce dropout rates and ensure every child has access to education.",
    user: "#BD2524",
  },
];

const NavigationButton = ({ direction, onClick }) => (
  <button
    className={`absolute top-[20%] -translate-y-1/2 z-10 flex items-center justify-center w-8 h-8 md:w-12 md:h-12 rounded-full bg-white/15 dark:bg-white/30 border border-[#4E6EB6] dark:border-white/50 hover:bg-white/25 dark:hover:bg-white/40 transition-colors ${
      direction === "next" ? "right-2 md:-right-16" : "left-2 md:-left-16"
    }`}
    aria-label={direction === "next" ? "Next slide" : "Previous slide"}
    onClick={onClick}
  >
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`md:w-4 md:h-4 ${direction === "next" ? "" : "rotate-180"}`}
    >
      <path
        d="M6 3L11 8L6 13"
        stroke="#374151"
        className="stroke-[#374151] dark:stroke-white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>
);

const CampaignNavigationButton = ({ direction, onClick }) => (
  <button
    className={`absolute top-[30%] -translate-y-1/2 z-10 flex items-center justify-center w-8 h-8 md:w-12 md:h-12 rounded-full bg-white/15 dark:bg-white/30 border border-[#4E6EB6] dark:border-white/50 hover:bg-white/25 dark:hover:bg-white/40 transition-colors ${
      direction === "next" ? "right-2 md:-right-16" : "left-2 md:-left-16"
    }`}
    aria-label={direction === "next" ? "Next slide" : "Previous slide"}
    onClick={onClick}
  >
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`md:w-4 md:h-4 ${direction === "next" ? "" : "rotate-180"}`}
    >
      <path
        d="M6 3L11 8L6 13"
        stroke="#374151"
        className="stroke-[#374151] dark:stroke-white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>
);

const FeaturedInitiatives = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [activeCampaignSlide, setActiveCampaignSlide] = useState(0);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [overlayPosition, setOverlayPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);
  const campaignContainerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };

    const handleScroll = () => {
      setHoveredCard(null);
    };

    checkMobile();
    // Initialize with first card as active
    setActiveSlide(0);

    window.addEventListener("resize", checkMobile);
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("resize", checkMobile);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Debounce function to prevent too many updates
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // Handle scroll for mobile only
  const handleScroll = debounce(() => {
    if (!containerRef.current || !isMobile) return;

    const container = containerRef.current;
    const scrollPosition = container.scrollLeft;
    const cardWidth = 242; // Fixed card width
    const containerPadding = 75.5; // (393 - 242) / 2 to center the card

    const newIndex = Math.round(
      (scrollPosition + containerPadding) / (cardWidth + containerPadding * 2),
    );
    setActiveSlide(Math.max(0, Math.min(newIndex, initiatives.length - 1)));
  }, 100);

  // Handle scroll for campaign section mobile only
  const handleCampaignScroll = debounce(() => {
    if (!campaignContainerRef.current || !isMobile) return;

    const container = campaignContainerRef.current;
    const scrollPosition = container.scrollLeft;
    const cardWidth = 231; // Fixed card width
    const containerPadding = 81; // (393 - 231) / 2 to center the card

    const newIndex = Math.round(
      (scrollPosition + containerPadding) / (cardWidth + containerPadding * 2),
    );
    setActiveCampaignSlide(Math.max(0, Math.min(newIndex, 3))); // 4 cards total (0-3)
  }, 100);

  // Handle card click for desktop
  const handleCardClick = (index) => {
    if (!isMobile) {
      setActiveSlide(index);
    }
  };

  // Handle campaign card click for desktop
  const handleCampaignCardClick = (index) => {
    if (!isMobile) {
      setActiveCampaignSlide(index);
    }
  };

  // Handle dot click
  const handleDotClick = (index) => {
    setActiveSlide(index);

    if (isMobile && containerRef.current) {
      const container = containerRef.current;
      const cardWidth = 242; // Fixed card width
      const containerPadding = 75.5; // (393 - 242) / 2 to center the card
      const newPosition = index * (cardWidth + containerPadding * 2);

      container.scrollTo({
        left: newPosition,
        behavior: "smooth",
      });
    }
  };

  // Handle campaign dot click
  const handleCampaignDotClick = (index) => {
    setActiveCampaignSlide(index);

    if (isMobile && campaignContainerRef.current) {
      const container = campaignContainerRef.current;
      const cardWidth = 231; // Fixed card width
      const containerPadding = 81; // (393 - 231) / 2 to center the card
      const newPosition = index * (cardWidth + containerPadding * 2);

      container.scrollTo({
        left: newPosition,
        behavior: "smooth",
      });
    }
  };

  // Position description overlay when hovering over a card
  const handleCardHover = (index, event) => {
    // Only show overlay on non-mobile devices
    if (isMobile) return;

    const card = event.currentTarget;
    const rect = card.getBoundingClientRect();
    setHoveredCard(index);
    setOverlayPosition({
      top: rect.top + rect.height / 2 + 50,
      left: rect.left + rect.width / 2,
    });
  };

  // Determine card dimensions - mobile single card, desktop varying sizes
  const getCardSizeClass = (index) => {
    if (isMobile) {
      return "h-[400px] w-[242px] flex-shrink-0 mx-auto"; // Fixed width with centering
    }
    // Horizontal layout with varying card widths based on Figma specifications
    if (index === 0 || index === 4) {
      return "w-[146px]"; // Smaller cards
    } else if (index === 2) {
      return "w-[249px]"; // Center card - largest
    } else {
      return "w-[244px]"; // Medium cards
    }
  };

  // Determine campaign card dimensions - mobile single card, desktop varying sizes
  const getCampaignCardSizeClass = (index) => {
    if (isMobile) {
      return "h-[400px] w-[231px] flex-shrink-0 mx-auto"; // Fixed width with centering
    }
    // Desktop layout - similar to main carousel but adjusted for campaign cards
    return "w-[231px]"; // Consistent width for campaign cards on desktop
  };

  return (
    <section className="py-16 md:py-20 bg-[#F4F5FB] dark:bg-jacarta-900 overflow-hidden">
      {/* Description overlay that appears on hover */}
      {hoveredCard !== null && (
        <div
          className="fixed z-[100] bg-white dark:bg-[#1F1E2D]/90 p-3 rounded-lg w-[388px] h-[184px] shadow-2xl transform -translate-x-1/2 -translate-y-1/2 pointer-events-none opacity-100"
          style={{
            top: `${overlayPosition.top}px`,
            left: `${overlayPosition.left}px`,
          }}
        >
          <p className="dark:text-[#EDEDED] tracking-wider leading-relaxed text-sm mb-6">
            {initiatives[hoveredCard]?.description}
            <br />
            <span className="block mt-6">THIS CAMPAIGN WILL BE LIVE SOON!</span>
          </p>
        </div>
      )}

      <div className="container relative mx-auto px-4 md:px-8">
        {/* Header Section */}
        <div className="text-center mb-8 md:mb-16 mt-4 md:mt-0">
          <h3 className="text-accent text-sm md:text-2xl font-medium tracking-[0.0214em] md:tracking-wider mb-2 md:mb-2 leading-[1.57] md:leading-normal">
            CAMPAIGNS
          </h3>
          <h2 className="text-[#374151] dark:text-[#EDEDED] font-bold text-xl md:text-5xl mb-3 md:mb-6 leading-[1.5] md:leading-normal">
            Featured Impact Campaigns
          </h2>
          <p className="text-[#374151] dark:text-[#EDEDED] text-xs md:text-base font-medium leading-[1.5] md:leading-snug max-w-6xl mx-auto tracking-[0.017em] md:tracking-normal">
            Check out the campaigns, support the ones you align with and build
            your impact portfolio. All campaigns are vetted, and you can follow
            their progress with reports and updates. Build connection with the
            brands, influencers and causes you believe in.
          </p>
        </div>

        {/* Carousel Section */}
        <div className="relative max-w-full w-full">
          <NavigationButton
            direction="prev"
            onClick={() => {
              const newIndex =
                activeSlide === 0 ? initiatives.length - 1 : activeSlide - 1;
              setActiveSlide(newIndex);
              if (isMobile && containerRef.current) {
                const container = containerRef.current;
                const cardWidth = 242; // Fixed card width
                const containerPadding = 75.5; // (393 - 242) / 2 to center the card
                const newPosition =
                  newIndex * (cardWidth + containerPadding * 2);
                container.scrollTo({
                  left: newPosition,
                  behavior: "smooth",
                });
              }
            }}
          />
          <NavigationButton
            direction="next"
            onClick={() => {
              const newIndex =
                activeSlide === initiatives.length - 1 ? 0 : activeSlide + 1;
              setActiveSlide(newIndex);
              if (isMobile && containerRef.current) {
                const container = containerRef.current;
                const cardWidth = 242; // Fixed card width
                const containerPadding = 75.5; // (393 - 242) / 2 to center the card
                const newPosition =
                  newIndex * (cardWidth + containerPadding * 2);
                container.scrollTo({
                  left: newPosition,
                  behavior: "smooth",
                });
              }
            }}
          />

          {/* Cards Container */}
          <div
            ref={containerRef}
            onScroll={handleScroll}
            className={`flex items-end gap-0 md:gap-2 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none justify-start md:justify-center w-full pb-4 md:pb-8 no-scrollbar scroll-smooth`}
            style={{
              scrollSnapType: isMobile ? "x mandatory" : "none",
              paddingLeft: isMobile ? "75.5px" : "0",
              paddingRight: isMobile ? "75.5px" : "0",
            }}
          >
            {initiatives.map((initiative, index) => (
              <div
                key={initiative.id}
                onClick={() => handleCardClick(index)}
                onMouseEnter={(e) => handleCardHover(index, e)}
                onMouseLeave={() => setHoveredCard(null)}
                className={`relative transition-all duration-500 ${isMobile ? "snap-center" : "snap-center"} cursor-pointer rounded-lg overflow-hidden bg-[rgba(231,233,244,0.94)] dark:bg-[#1F1E2D]/90 ${getCardSizeClass(
                  index,
                )} ${
                  index === activeSlide
                    ? "md:opacity-100 md:scale-100 md:z-10"
                    : "md:opacity-90 md:scale-95 hover:opacity-100"
                }`}
                style={{
                  marginRight:
                    isMobile && index < initiatives.length - 1 ? "151px" : "0",
                }}
              >
                <div
                  className="relative overflow-hidden rounded-t-lg"
                  style={{
                    height: isMobile
                      ? "320px"
                      : index === 0 || index === 4
                        ? "211px"
                        : index === 2
                          ? "329px"
                          : "292px",
                  }}
                >
                  <Image
                    src={initiative.image}
                    alt={initiative.title}
                    fill
                    className="object-cover object-center"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    priority
                  />
                </div>
                <div className="p-4 relative">
                  <h3
                    className="text-[#374151] dark:text-[#EDEDED] font-semibold mb-4 leading-tight"
                    style={{
                      fontSize: isMobile
                        ? "20px"
                        : index === 0 || index === 4
                          ? "14px"
                          : index === 2
                            ? "20px"
                            : "18px",
                    }}
                  >
                    {initiative.title}
                    <br />
                    {isMobile ? "Aunua Global" : "by"}
                  </h3>
                  <div
                    className="rounded-full absolute"
                    style={{
                      backgroundColor: initiative.user,
                      right: isMobile ? "16px" : index === 2 ? "50px" : "38px",
                      bottom: isMobile ? "34px" : index === 2 ? "32px" : "24px",
                      width: isMobile
                        ? "22px"
                        : index === 2
                          ? "22px"
                          : index === 0 || index === 4
                            ? "14px"
                            : "18px",
                      height: isMobile
                        ? "22px"
                        : index === 2
                          ? "22px"
                          : index === 0 || index === 4
                            ? "14px"
                            : "18px",
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>

          {/* Dots Navigation */}
          <div className="flex justify-center gap-5 mt-6 md:mt-16">
            {initiatives.map((_, index) => (
              <button
                key={index}
                onClick={() => handleDotClick(index)}
                className={`w-3 h-3 md:w-4 md:h-4 rounded-full transition-colors touch-none ${
                  index === activeSlide
                    ? "bg-[#4E6EB6]"
                    : "bg-[#D9D9D9] hover:bg-[#4E6EB6]/50"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Campaign Details Section */}
          <div className="relative max-w-full w-full mt-8 md:mt-16">
            {/* Campaign Navigation Buttons - Mobile Only */}
            {isMobile && (
              <>
                <CampaignNavigationButton
                  direction="prev"
                  onClick={() => {
                    const newIndex =
                      activeCampaignSlide === 0 ? 3 : activeCampaignSlide - 1; // 4 cards total (0-3)
                    setActiveCampaignSlide(newIndex);
                    if (isMobile && campaignContainerRef.current) {
                      const container = campaignContainerRef.current;
                      const cardWidth = 231; // Fixed card width
                      const containerPadding = 81; // (393 - 231) / 2 to center the card
                      const newPosition =
                        newIndex * (cardWidth + containerPadding * 2);
                      container.scrollTo({
                        left: newPosition,
                        behavior: "smooth",
                      });
                    }
                  }}
                />
                <CampaignNavigationButton
                  direction="next"
                  onClick={() => {
                    const newIndex =
                      activeCampaignSlide === 3 ? 0 : activeCampaignSlide + 1; // 4 cards total (0-3)
                    setActiveCampaignSlide(newIndex);
                    if (isMobile && campaignContainerRef.current) {
                      const container = campaignContainerRef.current;
                      const cardWidth = 231; // Fixed card width
                      const containerPadding = 81; // (393 - 231) / 2 to center the card
                      const newPosition =
                        newIndex * (cardWidth + containerPadding * 2);
                      container.scrollTo({
                        left: newPosition,
                        behavior: "smooth",
                      });
                    }
                  }}
                />
              </>
            )}

            <div className="flex justify-center gap-4 overflow-x-auto md:overflow-visible">
              <div
                ref={campaignContainerRef}
                onScroll={handleCampaignScroll}
                className={`flex items-end gap-0 md:gap-2 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none justify-start md:justify-center w-full pb-4 md:pb-8 no-scrollbar scroll-smooth`}
                style={{
                  scrollSnapType: isMobile ? "x mandatory" : "none",
                  paddingLeft: isMobile ? "81px" : "0", // (393 - 231) / 2 to center the card
                  paddingRight: isMobile ? "81px" : "0",
                }}
              >
                {[...Array(isMobile ? 4 : 4)].map((_, index) => (
                  <div
                    key={index}
                    onClick={() => handleCampaignCardClick(index)}
                    className={`relative transition-all duration-500 ${isMobile ? "snap-center" : "snap-center"} cursor-pointer rounded-lg overflow-hidden bg-white dark:bg-[#1F1E2D]/90 shadow-sm ${getCampaignCardSizeClass(
                      index,
                    )} ${
                      index === activeCampaignSlide
                        ? "md:opacity-100 md:scale-100 md:z-10"
                        : "md:opacity-90 md:scale-95 hover:opacity-100"
                    }`}
                    style={{
                      marginRight: isMobile && index < 3 ? "162px" : "0", // 393 - 231 = 162px spacing
                      height: isMobile ? "400px" : "auto", // Consistent height behavior
                    }}
                  >
                    <div
                      className="relative overflow-hidden rounded-t-lg"
                      style={{
                        height: isMobile ? "320px" : "230px", // Consistent with image heights
                      }}
                    >
                      <Image
                        src="/images/pudgy-penguin-campaign.jpg"
                        alt="Pudgy Penguin Campaign"
                        fill
                        className="object-cover object-center"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        priority
                      />
                    </div>
                    <div className="p-4 relative">
                      <h3
                        className="text-[#374151] dark:text-[#EDEDED] font-semibold mb-4 leading-tight"
                        style={{
                          fontSize: isMobile ? "20px" : "18px", // Consistent with main carousel logic
                        }}
                      >
                        Pudgy Penguin
                        <br />
                        {isMobile ? "Aunua Global" : "by"}
                      </h3>
                      <p className="text-[#374151] dark:text-[#EDEDED] font-medium text-sm leading-[1.29] md:leading-tight mb-1">
                        $10.00.
                        {!isMobile && <br />}
                        {isMobile ? " " : ""}AUNUA GLOBAL
                      </p>
                      <span className="text-[#4CAF50] font-medium text-xs leading-[1.5] block mt-1">
                        On-chain
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Campaign Dots Navigation - Mobile Only */}
            {isMobile && (
              <div className="flex justify-center gap-5 mt-6 md:mt-16">
                {[...Array(4)].map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleCampaignDotClick(index)}
                    className={`w-3 h-3 md:w-4 md:h-4 rounded-full transition-colors touch-none ${
                      index === activeCampaignSlide
                        ? "bg-[#4E6EB6]"
                        : "bg-[#D9D9D9] hover:bg-[#4E6EB6]/50"
                    }`}
                    aria-label={`Go to campaign slide ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Find more Campaigns Button */}
        <div className="flex justify-center mt-8 md:mt-16">
          <button className="bg-accent text-white font-normal text-base px-6 py-3 rounded-lg border-2 border-accent shadow-lg hover:bg-accent/90 transition-colors">
            Find more Campaigns
          </button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedInitiatives;
